import { SelectionEnum } from "../../enums";
import { toBookingCellIdParsedCell } from "../../helpers";
import { BookingCellId, DateYMDString } from "../../models";
import { selectedCellsBookingCalendarAtom } from "../atoms";
import { boundedSelector } from "../selector-factories";
import {
  datesArrayCalendarBookingsSF,
  visiblePassengersIdsBookingSelector,
} from "./booking.calendar.selectors";

interface IBookingCalendarData {
  allCells: Set<BookingCellId>;
  cols: Map<DateYMDString, Set<BookingCellId>>;
  rows: Map<number, Set<BookingCellId>>;
}

export const bookingCalendarDataSelector =
  boundedSelector<IBookingCalendarData>({
    key: "bookingCalendarDataSelector",
    get: ({ get }) => {
      const passengersIds = get(visiblePassengersIdsBookingSelector);
      const datesArray = get(datesArrayCalendarBookingsSF);
      const defaultResult: IBookingCalendarData = {
        allCells: new Set<BookingCellId>(),
        cols: new Map<DateYMDString, Set<BookingCellId>>(),
        rows: new Map<number, Set<BookingCellId>>(),
      };

      if (!passengersIds?.length || !datesArray?.length) {
        return defaultResult;
      }

      // This loop is critical for performance
      const result = passengersIds.reduce((acc, passengerId) => {
        datesArray.forEach(date => {
          const bookingCellId = toBookingCellIdParsedCell({
            date,
            passengerId,
          });
          acc.allCells.add(bookingCellId);
          const dateYMDString = String(date) as DateYMDString;
          const colsSet = acc.cols.get(dateYMDString) ?? new Set();
          const rowsSet = acc.rows.get(passengerId) ?? new Set();

          acc.cols.set(dateYMDString, colsSet.add(bookingCellId));
          acc.rows.set(passengerId, rowsSet.add(bookingCellId));
        });
        return acc;
      }, defaultResult);

      return result;
    },
  });

export const allBookingCalendarCells = boundedSelector<Set<BookingCellId>>({
  key: "allCellsBookingCalendar",
  get: ({ get }) => {
    return get(bookingCalendarDataSelector).allCells;
  },
});

export const allRecordsSelectedBookingCalendar = boundedSelector<SelectionEnum>(
  {
    key: "allRecordsSelectedBookingCalendar",
    get: ({ get }) => {
      const selectedCellsIds = get(selectedCellsBookingCalendarAtom);

      if (!selectedCellsIds.size) {
        return SelectionEnum.None;
      }

      const allCells = get(allBookingCalendarCells);

      const isAllSelected = allCells.size === selectedCellsIds.size;

      return isAllSelected ? SelectionEnum.All : SelectionEnum.Some;
    },
  },
);
